/**
 * Local draft attachment state for the agentic-room composer.
 *
 * Lifecycle:
 *   1. Component mounts — `stagingId` is generated once per draft.
 *   2. User picks file(s) — each goes into `items` with `status: 'uploading'`.
 *   3. We presign + PUT in parallel; on success the item flips to `ready`
 *      and we keep its descriptor (bucket/s3Key/filename/...). The execute
 *      payload reads `descriptors` directly.
 *   4. After Send succeeds the parent calls `reset()` which (a) clears
 *      `items` and (b) rotates the `stagingId` so a follow-up draft does
 *      not collide with the keys the backend just moved.
 *   5. After Send fails, the parent does *not* reset — the user still has
 *      their text + attachments and can retry.
 */
import { useCallback, useMemo, useRef, useState } from 'react';
import { roomUploadRejection } from '@stepflow/ui-shared/utils/roomUploads.js';
import {
  newAttachmentStagingId,
  uploadAttachmentForStaging,
} from '@/services/networkAttachmentApi';

let __nextItemId = 1;

function freshItem(file) {
  __nextItemId += 1;
  return {
    id: `att-${__nextItemId}-${Date.now().toString(36)}`,
    file,
    filename: file.name,
    contentType: file.type || 'application/octet-stream',
    size: file.size,
    status: 'uploading',
    percent: 0,
    descriptor: null,
    error: null,
  };
}

export function useChatAttachments(networkId, { onUploaded } = {}) {
  const [items, setItems] = useState([]);
  const stagingIdRef = useRef(newAttachmentStagingId());
  // Ref so ``addFiles``'s identity doesn't churn when the parent's callback changes.
  const onUploadedRef = useRef(onUploaded);
  onUploadedRef.current = onUploaded;

  const updateItem = useCallback((id, patch) => {
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...patch } : it)));
  }, []);

  const addFiles = useCallback(
    async (files) => {
      const list = Array.from(files || []);
      if (!list.length || !networkId) return;
      // the DOOR (CONTEXT-DOCS.md §P0): a type we can't read / too big → an error chip with the reason, never uploaded
      const rejected = [];
      const queued = [];
      for (const f of list) {
        const why = roomUploadRejection(f);
        if (why) rejected.push({ ...freshItem(f), status: 'error', error: why });
        else queued.push(freshItem(f));
      }
      setItems((prev) => [...prev, ...rejected, ...queued]);
      if (!queued.length) return;
      const stagingId = stagingIdRef.current;
      const readyDescriptors = [];
      await Promise.all(
        queued.map(async (it) => {
          try {
            const descriptor = await uploadAttachmentForStaging(networkId, it.file, stagingId, {
              onProgress: ({ percent }) => updateItem(it.id, { percent }),
            });
            updateItem(it.id, { status: 'ready', descriptor, percent: 100 });
            if (descriptor) readyDescriptors.push(descriptor);
          } catch (err) {
            updateItem(it.id, {
              status: 'error',
              error: err?.message || 'Upload failed',
            });
          }
        }),
      );
      // §26.4 — uploads for this batch are in S3; let the parent kick the room-uploads
      // consolidation NOW (overlapping the user typing) so context is ready by Send. Best-effort.
      if (readyDescriptors.length && onUploadedRef.current) {
        try {
          onUploadedRef.current(readyDescriptors, stagingId);
        } catch { /* best-effort — never break the attach flow */ }
      }
    },
    [networkId, updateItem],
  );

  const removeItem = useCallback((id) => {
    setItems((prev) => prev.filter((it) => it.id !== id));
  }, []);

  const reset = useCallback(() => {
    setItems([]);
    stagingIdRef.current = newAttachmentStagingId();
  }, []);

  const descriptors = useMemo(
    () => items.filter((it) => it.status === 'ready' && it.descriptor).map((it) => it.descriptor),
    [items],
  );
  const hasUploading = useMemo(() => items.some((it) => it.status === 'uploading'), [items]);
  const hasError = useMemo(() => items.some((it) => it.status === 'error'), [items]);

  return {
    items,
    addFiles,
    removeItem,
    reset,
    stagingId: stagingIdRef.current,
    descriptors,
    hasUploading,
    hasError,
  };
}
